import { BadgePercent, Bike, ChefHat, Store, UserRound } from "lucide-react";
import PartnerPage from "../partner/PartnerPage";
import { DashBoardLink } from "../../Helper/config";
import sellerStripImg from "../../assets/seller-strip.webp";
import homeKitchenImg from "../../assets/home-kitchen.jpg";
import deliveryImg from "../../assets/delivery.jpg";
import oppBiryaniImg from "../../assets/seller-opp-biryani.webp";
import oppCateringImg from "../../assets/seller-opp-catering.webp";
import oppRecipesImg from "../../assets/seller-opp-recipes.webp";

const LOGIN_URL = `${DashBoardLink}/login`;

/**
 * Every number here maps to something the platform actually does:
 * 15% commission and the 10-day earning hold come from the earnings service,
 * the five steps and the document checklist come from the dashboard's own
 * "Become a Seller" guide.
 */
const content = {
  hero: {
    title: "Turn your kitchen into a food business",
    subtitle:
      "Cook what you already cook well, and sell it to your whole city. OwnFood brings you the orders, the payments, and the riders. You keep the kitchen.",
    cta: { label: "Get started", href: LOGIN_URL },
  },

  strip: {
    img: sellerStripImg,
    alt: "A home chef holding a pan and spatula, ready to cook",
  },

  why: {
    heading: "Why sell on OwnFood?",
    sub: "A marketplace built only for home-cooked food, not a restaurant app with a home-cook section bolted on.",
    items: [
      {
        icon: ChefHat,
        title: "Your kitchen, your rules",
        desc: "You set the menu, the prices, and the hours you cook. Nobody tells you what to put on the stove.",
      },
      {
        icon: Store,
        title: "Customers beyond your street",
        desc: "Your kitchen shows up in search, categories, and nearby listings, so food lovers across the city can find you.",
      },
      {
        icon: BadgePercent,
        title: "You only pay when you sell",
        desc: "There is no joining fee and no monthly fee. OwnFood keeps 15% commission on an order, and nothing at all when you do not sell.",
      },
    ],
  },

  gettingStarted: {
    img: homeKitchenImg,
    alt: "A clean home kitchen with pots on the stove",
    heading: "Getting started",
    body: "Registration happens in the OwnFood dashboard. You fill in your owner details, build your kitchen profile, and tell riders how to collect from you. Our team reviews the application and notifies you the moment your kitchen is approved, and then you can publish your first dish.",
    cta: { label: "Start your application", href: LOGIN_URL },
  },

  steps: {
    heading: "Five steps to your first order",
    sub: "Everything is done from the dashboard with the same login you use to order food.",
    items: [
      {
        title: "Create a buyer account",
        desc: "Register on ownfood.com. Every seller starts as a buyer, so if you already order on OwnFood, skip ahead.",
        link: {
          label: "ownfood.com/authentication/register",
          href: "https://ownfood.com/authentication/register",
        },
      },
      {
        title: "Log in to the dashboard",
        desc: "Open dashboard.ownfood.com and sign in with the same email and password. There is no second account to create.",
        link: {
          label: "dashboard.ownfood.com/login",
          href: LOGIN_URL,
        },
      },
      {
        title: "Open the seller application",
        desc: "In the dashboard sidebar, go to Application → Become a Seller.",
      },
      {
        title: "Fill in the form",
        desc: "Owner details, kitchen profile, and pickup instructions for riders. Keep your NID photos handy.",
      },
      {
        title: "Wait for approval",
        desc: "Our team reviews your application and notifies you once your kitchen is live. Then you add dishes and start taking orders.",
      },
    ],
  },

  checklist: {
    heading: "What to keep ready",
    sub: "The application asks for three groups of information. Collect them first and the form takes minutes.",
    groups: [
      {
        icon: UserRound,
        title: "Owner details",
        items: [
          "Full name",
          "Phone number and email",
          "Date of birth",
          "NID number",
          "Photos of your NID, front and back",
          "A profile photo",
          "TIN number (optional)",
        ],
      },
      {
        icon: ChefHat,
        title: "Kitchen profile",
        items: [
          "Kitchen name and phone number",
          "Address and kitchen logo",
          "Speciality and cuisines you cook",
          "Dietary restrictions you can handle",
          "A short description of you as a chef",
        ],
      },
      {
        icon: Bike,
        title: "Rider notes",
        items: [
          "Day shift pickup instructions",
          "Night shift pickup instructions",
          "How to find your pickup point",
        ],
      },
    ],
  },

  band: {
    kicker: "Earnings",
    heading: "You keep 85% of every order",
    body: "Earnings are recorded per delivered order, so you always see what a day of cooking actually paid.",
    bullets: [
      "OwnFood commission is 15% of the order value, including the delivery fee",
      "Platform discounts and coupons are paid by OwnFood and never reduce your payout",
      "Each earning clears 10 days after the order, then it is yours to withdraw",
      "Withdraw to bKash, Nagad, Rocket, or straight to your bank account",
    ],
    img: deliveryImg,
    alt: "A delivery rider carrying an order through the city",
    cta: { label: "Become a seller", href: LOGIN_URL },
  },

  cards: {
    heading: "More ways to earn from one kitchen",
    sub: "Your OwnFood kitchen is not limited to daily menu items.",
    items: [
      {
        img: oppBiryaniImg,
        alt: "A bowl of home-cooked chicken biryani ready to serve",
        title: "Daily menu and pre-orders",
        desc: "Publish today's dishes or take pre-orders for the week ahead.",
      },
      {
        img: oppCateringImg,
        alt: "Meal boxes packed side by side for a catering order",
        title: "Catering and wish orders",
        desc: "Take catering jobs and answer wish orders at your own price.",
      },
      {
        img: oppRecipesImg,
        alt: "A plate of home-baked chocolate chip cookies",
        title: "Publish your recipes",
        desc: "Publish recipes on OwnFood and build a following that orders.",
      },
    ],
  },

  faq: {
    heading: "FAQ",
    items: [
      {
        q: "What does it cost to join?",
        a: "Nothing. Registering a kitchen is free and there is no monthly fee. OwnFood keeps a 15% commission on each order, so if you do not sell, you do not pay.",
      },
      {
        q: "Do I need a buyer account first?",
        a: "Yes. A buyer account on ownfood.com is the prerequisite for a seller application. You then log in to the dashboard with the same email and password.",
      },
      {
        q: "When do I get paid?",
        a: "An earning is created for every delivered order. It clears 10 days after the order, and you can then withdraw the balance to bKash, Nagad, Rocket, or your bank account.",
      },
      {
        q: "Do OwnFood discounts come out of my money?",
        a: "No. When a customer uses an OwnFood coupon, the platform pays for that discount. Your commission and payout are calculated on the full order value.",
      },
      {
        q: "Who delivers the food?",
        a: "OwnFood riders do. You cook and hand the order over at your pickup point. The day and night pickup instructions you enter in the application tell riders exactly how to reach you.",
      },
      {
        q: "Can I decide my own menu, prices, and hours?",
        a: "Yes. You control your dishes, your prices, and when your kitchen is open. Take the day off and nothing happens to your account.",
      },
      {
        q: "How long does approval take?",
        a: "Our team reviews each application manually and notifies you as soon as it is approved. A complete application with readable NID photos moves fastest.",
      },
      {
        q: "What if I need help after joining?",
        a: "Raise a support ticket from your account and a real person answers. You can also reach us from the contact page.",
      },
    ],
  },

  contact: {
    heading: "Still have questions?",
    body: "Tell us about your kitchen and we will walk you through the application.",
    cta: { label: "Contact us", href: "/contact-us" },
  },
};

export default function BecomeSeller() {
  return <PartnerPage content={content} />;
}
